import { useEffect, useMemo, useState } from 'react'

import { OperationStateBadge } from '@/components/operations/OperationProgress'
import { ApplyConfirmation } from '@/components/storefronts/ApplyConfirmation'
import { StorefrontModal } from '@/components/storefronts/StorefrontModal'
import { announceOperation, useWatchedOperation } from '@/hooks/useOperations'
import { listAllOperationItems } from '@/lib/api/client/operations'
import { applyYmPricing, confirmYmQuarantine, previewYmPricing } from '@/lib/api/client/ymarket-pricing'
import type { Operation, OperationItem } from '@/lib/api/schemas/operations.schema'
import type {
  YmCodedMessage,
  YmOfferPricing,
  YmPricePoint,
  YmPricingPreview,
  YmPricingPreviewRequest,
  YmPricingPreviewRow,
} from '@/lib/api/schemas/ymarket-pricing.schema'
import { formatRubleAmount, formatRublePerGram, pluralPieces } from '@/lib/market-money'
import { isActiveOperation } from '@/lib/operations/labels'
import { unavailableReasonText } from '@/lib/warehouse/labels'
import {
  FORGE_UNSET,
  QUARANTINE_RISK_EXPLANATION,
  RECONCILE_EXPLANATIONS,
  RECONCILE_LABELS,
  calculateClientEarnings,
  formatChangePercent,
} from '@/lib/ymarket-pricing'

const QUARANTINED_REASON_CODE = 'price_quarantined'

function errorText(cause: unknown, fallback: string): string {
  return cause instanceof Error && cause.message.trim() !== '' ? cause.message : fallback
}

function isApplicable(row: YmPricingPreviewRow): boolean {
  return row.after !== null && row.refusals.length === 0
}

function pointMoney(value: number | null): string {
  return value === null ? FORGE_UNSET : formatRubleAmount(value)
}

function PricePointCell({ point, compare }: { point: YmPricePoint | null; compare?: YmPricePoint }) {
  if (point === null) return <span className="text-ink-muted">Не изменится</span>
  const changed = (value: number | null, previous: number | null | undefined) => compare !== undefined && value !== previous
  const participating = point.promoParticipation.filter((promo) => promo.participating).map((promo) => promo.name)
  return (
    <span className="flex min-w-[130px] flex-col gap-0.5 tab-nums">
      <span className={changed(point.price, compare?.price) ? 'font-semibold text-ink' : ''}>{pointMoney(point.price)}</span>
      <span className={`text-[11px] ${changed(point.discountBase, compare?.discountBase) ? 'font-semibold text-ink-2' : 'text-ink-muted'}`}>
        зачёркнутая {point.discountBase === null ? FORGE_UNSET : <span className="line-through">{formatRubleAmount(point.discountBase)}</span>}
      </span>
      <span className={`text-[11px] ${changed(point.minimumForPromos, compare?.minimumForPromos) ? 'font-semibold text-ink-2' : 'text-ink-muted'}`}>
        мин. для акций {pointMoney(point.minimumForPromos)}
      </span>
      {point.promoParticipation.length > 0 ? (
        <span className="text-[11px] text-ink-muted">акции: {participating.length > 0 ? participating.join(', ') : 'не участвует'}</span>
      ) : null}
    </span>
  )
}

function earningsText(offer: YmOfferPricing | undefined, price: number | null): string {
  if (offer === undefined || price === null) return '—'
  const earnings = calculateClientEarnings(offer, price)
  if (earnings.earnings === null || earnings.earningsPercent === null) {
    return earnings.unavailableReason === null ? '—' : `— (${unavailableReasonText(earnings.unavailableReason)})`
  }
  return `${formatRubleAmount(earnings.earnings)} · ${earnings.earningsPercent.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} %`
}

function MessageList({ label, tone, entries }: { label: string; tone: string; entries: readonly { offer: string; message: YmCodedMessage }[] }) {
  if (entries.length === 0) return null
  return (
    <section aria-label={label} className="space-y-1">
      <h3 className="text-[13px] font-semibold text-ink">{label}</h3>
      <ul className={`space-y-0.5 text-[13px] leading-5 ${tone}`}>
        {entries.map(({ offer, message }, index) => (
          <li key={`${offer}:${message.code}:${index}`} data-code={message.code}>{offer}: {message.message}</li>
        ))}
      </ul>
    </section>
  )
}

type Stage =
  | { kind: 'loading' }
  | { kind: 'ready'; preview: YmPricingPreview }
  | { kind: 'failed'; message: string }

type Tracking =
  | { kind: 'idle' }
  | { kind: 'tracking'; operation: Operation }
  | { kind: 'settled'; operation: Operation; quarantined: string[] }

/** Offers the operation put into YM quarantine, limited to offers this preview flagged as at risk. */
function quarantinedOffers(items: readonly OperationItem[], preview: YmPricingPreview, offers: readonly YmOfferPricing[]): string[] {
  const flagged = new Set(preview.rows.filter((row) => row.quarantineRisk).map((row) => row.offerId))
  const offerByProduct = new Map(offers.map((offer) => [offer.productId, offer.offerId]))
  const reported = items
    .filter((item) => item.reasonCode === QUARANTINED_REASON_CODE)
    .map((item) => item.marketplaceIds.offerId ?? offerByProduct.get(item.subjectId))
    .filter((offerId): offerId is string => offerId !== undefined && flagged.has(offerId))
  return [...new Set(reported)]
}

export function PricingPreviewDialog({
  request,
  offers,
  title,
  marketplaces = 'Яндекс Маркет',
  confirmationAcknowledgement,
  confirmationText,
  offerLabels,
  onClose,
}: {
  request: YmPricingPreviewRequest
  offers: readonly YmOfferPricing[]
  title: string
  marketplaces?: string
  confirmationAcknowledgement?: string
  confirmationText?: string
  offerLabels?: Readonly<Record<string, string>>
  onClose: (applied: boolean) => void
}) {
  const [stage, setStage] = useState<Stage>({ kind: 'loading' })
  const [generation, setGeneration] = useState(0)
  const [riskAccepted, setRiskAccepted] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tracking, setTracking] = useState<Tracking>({ kind: 'idle' })
  const [confirmation, setConfirmation] = useState<Operation | null>(null)
  const [now, setNow] = useState(() => Date.now())

  const labels = useMemo(() => new Map(offers.map((offer) => [offer.offerId, offer.label])), [offers])
  const offerById = useMemo(() => new Map(offers.map((offer) => [offer.offerId, offer])), [offers])
  const labelOf = (offerId: string) => labels.get(offerId) ?? offerLabels?.[offerId] ?? offerId

  useEffect(() => {
    const controller = new AbortController()
    setStage({ kind: 'loading' })
    setRiskAccepted(false)
    setConfirming(false)
    setError(null)
    previewYmPricing(request, controller.signal)
      .then((preview) => { if (!controller.signal.aborted) setStage({ kind: 'ready', preview }) })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) setStage({ kind: 'failed', message: errorText(cause, 'Не удалось подготовить предпросмотр цен.') })
      })
    return () => controller.abort()
  }, [request, generation])

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 15_000)
    return () => window.clearInterval(timer)
  }, [])

  const preview = stage.kind === 'ready' ? stage.preview : null
  const trackedOperationId = tracking.kind === 'tracking' ? tracking.operation.id : null

  const watched = useWatchedOperation(preview === null ? null : trackedOperationId)

  useEffect(() => {
    if (watched === null || preview === null || watched.id !== trackedOperationId) return undefined
    if (isActiveOperation(watched)) {
      setTracking({ kind: 'tracking', operation: watched })
      return undefined
    }
    const controller = new AbortController()
    listAllOperationItems(watched.id, controller.signal)
      .then((items) => {
        if (!controller.signal.aborted) setTracking({ kind: 'settled', operation: watched, quarantined: quarantinedOffers(items, preview, offers) })
      })
      .catch(() => undefined)
    return () => controller.abort()
  }, [offers, preview, trackedOperationId, watched])

  const rows = preview?.rows ?? []
  const applicable = rows.filter(isApplicable)
  const risky = rows.filter((row) => row.quarantineRisk)
  const refused = rows.filter((row) => row.refusals.length > 0)
  const expired = preview !== null && Date.parse(preview.expiresAt) <= now
  const applied = tracking.kind !== 'idle'
  const applicableNow = preview !== null && applicable.length > 0 && !expired && !applied && (risky.length === 0 || riskAccepted)
  const canApply = applicableNow && !busy

  useEffect(() => {
    if (!applicableNow) setConfirming(false)
  }, [applicableNow])

  const requestApply = () => {
    if (canApply) setConfirming(true)
  }

  const confirmApply = async () => {
    if (!confirming || !canApply || preview === null) return
    setBusy(true)
    setError(null)
    try {
      const operation = await applyYmPricing(preview.previewHash)
      announceOperation(operation)
      setTracking({ kind: 'tracking', operation })
      setConfirming(false)
    } catch (cause: unknown) {
      setError(errorText(cause, 'Не удалось поставить изменение цен в очередь.'))
    } finally {
      setBusy(false)
    }
  }

  const confirmQuarantine = async (offerIds: string[]) => {
    if (preview === null || offerIds.length === 0 || busy) return
    setBusy(true)
    setError(null)
    try {
      const operation = await confirmYmQuarantine({ offerIds, previewHash: preview.previewHash })
      announceOperation(operation)
      setConfirmation(operation)
    } catch (cause: unknown) {
      setError(errorText(cause, 'Не удалось подтвердить цены в карантине.'))
    } finally {
      setBusy(false)
    }
  }

  const reconcileNote = 'reconcile' in request ? RECONCILE_EXPLANATIONS[request.reconcile.direction] : null
  const warnings = rows.flatMap((row) => row.warnings.map((message) => ({ offer: labelOf(row.offerId), message })))
  const refusals = refused.flatMap((row) => row.refusals.map((message) => ({ offer: labelOf(row.offerId), message })))
  const toForge = 'reconcile' in request && request.reconcile.direction === 'ym_to_forge'

  return (
    <StorefrontModal title={title} labelledBy="ym-pricing-preview-title" wide busy={busy} onClose={() => onClose(applied)} footer={(
      <>
        {confirming && applicableNow ? (
          <ApplyConfirmation
            testId="ym-pricing-confirm"
            acknowledgement={confirmationAcknowledgement ?? (toForge ? 'Записать цены Маркета в Forge' : `Отправить новые цены на ${marketplaces}`)}
            busy={busy}
            onConfirm={() => { void confirmApply() }}
            onCancel={() => setConfirming(false)}
          >
            {confirmationText ?? `Подтвердите: ${title} — ${marketplaces}, ${pluralPieces(applicable.length)}. ${toForge ? 'На Маркет ничего не отправляется.' : 'Запрос поставщику уйдёт только после второго подтверждения.'}`}
          </ApplyConfirmation>
        ) : null}
        <button type="button" className="btn btn-ghost" onClick={() => onClose(applied)}>{applied ? 'Закрыть' : 'Отмена'}</button>
        {expired && !applied ? (
          <button type="button" className="btn btn-ghost" onClick={() => setGeneration((value) => value + 1)}>Пересчитать предпросмотр</button>
        ) : null}
        {!applied ? (
          <button type="button" className="btn btn-primary" disabled={!canApply || confirming} onClick={requestApply}>
            {expired ? 'Предпросмотр устарел' : `Применить (${applicable.length})`}
          </button>
        ) : null}
      </>
    )}>
      <div className="space-y-4" data-testid="ym-pricing-preview">
        {reconcileNote !== null && 'reconcile' in request ? (
          <p className="rounded-md bg-bone/60 px-3 py-2 text-[13px] text-ink-2">
            <b className="font-semibold">{RECONCILE_LABELS[request.reconcile.direction]}.</b> {reconcileNote}
          </p>
        ) : null}
        {stage.kind === 'loading' ? <p role="status" className="text-sm text-ink-muted">Готовим предпросмотр…</p> : null}
        {stage.kind === 'failed' ? <p role="alert" className="rounded-md bg-st-unpaid/10 p-3 text-sm text-st-unpaid">{stage.message}</p> : null}
        {preview !== null ? (
          <>
            <p className="rounded-md bg-bone/60 px-3 py-2 text-sm font-semibold tab-nums">
              Изменится: {applicable.length} · С риском карантина: {risky.length} · Отказано: {refused.length}
            </p>
            {risky.length > 0 ? (
              <div role="alert" data-testid="quarantine-risk-alert" className="space-y-2 rounded-md border border-st-unpaid/40 bg-st-unpaid/10 p-3 text-[13px] leading-5 text-ink">
                <p>{QUARANTINE_RISK_EXPLANATION}</p>
                <p className="text-ink-2">Под риском: {risky.map((row) => labelOf(row.offerId)).join(', ')}.</p>
                {!applied ? (
                  <label className="flex items-center gap-2 font-medium">
                    <input type="checkbox" checked={riskAccepted} onChange={(event) => setRiskAccepted(event.target.checked)} />
                    Понимаю риск карантина, применить всё равно
                  </label>
                ) : null}
              </div>
            ) : null}
            <div className="overflow-x-auto rounded-lg border border-rule">
              <table className="min-w-full text-left text-xs">
                <thead className="bg-bone/60 text-ink-2">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Изделие</th>
                    <th className="px-3 py-2 font-semibold">Сейчас</th>
                    <th className="px-3 py-2 font-semibold">Станет</th>
                    <th className="px-3 py-2 font-semibold">За грамм</th>
                    <th className="px-3 py-2 font-semibold">Изменение</th>
                    <th className="px-3 py-2 font-semibold">Заработок</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.offerId}
                      data-offer-id={row.offerId}
                      data-quarantine-risk={row.quarantineRisk ? 'true' : 'false'}
                      className={`border-t border-rule align-top ${row.quarantineRisk ? 'bg-st-unpaid/5 shadow-[inset_3px_0_0_var(--color-st-unpaid)]' : ''} ${row.refusals.length > 0 ? 'text-ink-muted' : ''}`}
                    >
                      <td className="px-3 py-2 font-medium">
                        <span className="block text-ink">{labelOf(row.offerId)}</span>
                        {row.quarantineRisk ? <span className="mt-1 inline-flex rounded-full bg-st-unpaid/15 px-2 py-0.5 text-[11px] font-semibold text-st-unpaid">Риск карантина</span> : null}
                        {row.refusals.length > 0 ? <span className="mt-1 inline-flex rounded-full bg-bone px-2 py-0.5 text-[11px] font-semibold text-ink-2">Отказано</span> : null}
                      </td>
                      <td className="px-3 py-2"><PricePointCell point={row.before} /></td>
                      <td className="px-3 py-2"><PricePointCell point={row.after} compare={row.before} /></td>
                      <td className="px-3 py-2 tab-nums">{row.pricePerGram === null ? <span className="text-ink-muted">вес не измерен</span> : formatRublePerGram(row.pricePerGram)}</td>
                      <td className="px-3 py-2 tab-nums">{formatChangePercent(row.changePercent)}</td>
                      <td className="px-3 py-2 tab-nums">{earningsText(offerById.get(row.offerId), row.before.price)} → {row.after === null ? '—' : earningsText(offerById.get(row.offerId), row.after.price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <MessageList label="Замечания" tone="text-gold-deep" entries={warnings} />
            <MessageList label="Отказы" tone="text-st-unpaid" entries={refusals} />
            {!applied ? (
              <p className="text-[12px] text-ink-muted">
                Предпросмотр действителен до {new Date(preview.expiresAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}. Цены уйдут только после «Применить».
              </p>
            ) : null}
          </>
        ) : null}
        {error !== null ? <p role="alert" className="rounded-md bg-st-unpaid/10 p-3 text-sm text-st-unpaid">{error}</p> : null}
        {tracking.kind !== 'idle' ? (
          <section aria-label="Применение цен" className="space-y-2 rounded-lg border border-rule p-3 text-[13px]">
            <p className="flex flex-wrap items-center gap-2">
              <span>Операция «{tracking.operation.title}» поставлена в очередь. Ход выполнения — в окне «Операции».</span>
              <OperationStateBadge state={tracking.operation.state} />
            </p>
            {tracking.kind === 'settled' && tracking.quarantined.length > 0 ? (
              <div data-testid="quarantine-confirm" className="space-y-2 rounded-md border border-st-unpaid/40 bg-st-unpaid/10 p-3">
                <p className="font-semibold text-ink">Маркет отправил цены в карантин: {tracking.quarantined.map(labelOf).join(', ')}.</p>
                <p className="text-ink-2">Пока цена не подтверждена, предложение скрыто с витрины. Подтвердите, если новая цена верна.</p>
                {confirmation === null ? (
                  <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => { void confirmQuarantine(tracking.quarantined) }}>
                    Подтвердить цены в карантине ({tracking.quarantined.length})
                  </button>
                ) : (
                  <p role="status">Подтверждение поставлено в очередь: «{confirmation.title}».</p>
                )}
              </div>
            ) : null}
          </section>
        ) : null}
      </div>
    </StorefrontModal>
  )
}
